import React, {useEffect} from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  useTheme,
  alpha,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  Search,
  Campaign,
  People,
  Analytics,
  Email,
  Instagram,
  Facebook,
  LinkedIn,
  Twitter,
  CheckCircle,
  Speed,
  BarChart,
} from '@mui/icons-material';


const softShadow = '0 8px 24px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.02)';
const softShadowHover = '0 12px 28px rgba(0,0,0,0.06), 0 4px 8px rgba(0,0,0,0.02)';

const DigitalMarketing = () => {
  const theme = useTheme();

  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#process') {
      const element = document.getElementById('process');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      icon: <Search fontSize="large" />,
      title: 'SEO (Search Engine Optimization)',
      desc: 'Boost your organic visibility with on‑page, off‑page, and technical SEO strategies that drive targeted traffic.',
    },
    {
      icon: <Campaign fontSize="large" />,
      title: 'PPC & Paid Advertising',
      desc: 'Google Ads, Facebook Ads, and LinkedIn Ads campaigns optimized for maximum ROI.',
    },
    {
      icon: <People fontSize="large" />,
      title: 'Social Media Marketing',
      desc: 'Grow your brand presence on Instagram, Facebook, LinkedIn, Twitter, and TikTok.',
    },
    {
      icon: <Analytics fontSize="large" />,
      title: 'Analytics & Data Insights',
      desc: 'Measure what matters with custom dashboards, conversion tracking, and actionable reports.',
    },
    {
      icon: <Email fontSize="large" />,
      title: 'Email Marketing',
      desc: 'Engage your audience with automated email campaigns, newsletters, and drip sequences.',
    },
    {
      icon: <TrendingUp fontSize="large" />,
      title: 'Content Marketing',
      desc: 'Blog posts, whitepapers, case studies, and video content that builds authority and trust.',
    },
  ];

  const benefits = [
    { title: 'Data‑Driven Strategy', desc: 'Every decision backed by analytics and performance data.' },
    { title: 'Multi‑Channel Approach', desc: 'Integrated campaigns across SEO, paid ads, social, and email.' },
    { title: 'Transparent Reporting', desc: 'Monthly reports with clear metrics and ROI analysis.' },
    { title: 'Dedicated Account Manager', desc: 'A single point of contact for all your marketing needs.' },
  ];

  const platforms = [
    { name: 'Google Ads', icon: <Search fontSize="small" /> },
    { name: 'Facebook / Instagram Ads', icon: <Facebook fontSize="small" /> },
    { name: 'LinkedIn Ads', icon: <LinkedIn fontSize="small" /> },
    { name: 'Twitter Ads', icon: <Twitter fontSize="small" /> },
    { name: 'Email Marketing (Mailchimp, Klaviyo)', icon: <Email fontSize="small" /> },
    { name: 'Google Analytics 4', icon: <Analytics fontSize="small" /> },
    { name: 'Google Tag Manager', icon: <Analytics fontSize="small" /> },
    { name: 'Social Media Management (Buffer, Hootsuite)', icon: <People fontSize="small" /> },
  ];

  const process = [
    { step: '01', title: 'Discovery', desc: 'Understand your business, audience, and competition.' },
    { step: '02', title: 'Strategy', desc: 'Develop a multi‑channel marketing plan with clear goals.' },
    { step: '03', title: 'Execution', desc: 'Launch campaigns with continuous optimization.' },
    { step: '04', title: 'Measurement', desc: 'Monitor performance and adjust for maximum impact.' },
  ];

  return (
    <Box>
      {/* Hero Section with Circular Image */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Digital Marketing"
                icon={<TrendingUp />}
                sx={{
                  mb: 2,
                  bgcolor: alpha(theme.palette.common.white, 0.15),
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' } }}
              >
                Drive Growth with Data‑Driven Marketing
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                SEO, PPC, social media, content marketing, and analytics – all
                designed to attract, engage, and convert your target audience.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={Link}
                  to="/request-quote"
                  variant="contained"
                  size="large"
                  sx={{ bgcolor: 'white', color: 'primary.main', px: 4 }}
                >
                  Grow Your Business
                </Button>
                <Button
                  component={Link}
                  to="#process"
                  variant="outlined"
                  size="large"
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  How It Works
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 450,
                  aspectRatio: '1/1',
                  margin: 'auto',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  boxShadow: '0 20px 35px -10px rgba(0,0,0,0.3)',
                  animation: 'float 6s ease-in-out infinite',
                  '@keyframes float': {
                    '0%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-20px)' },
                    '100%': { transform: 'translateY(0px)' },
                  },
                }}
              >
                <Box
                  component="img"
                  src="https://placehold.co/600x500/4F46E5/white?text=Digital+Marketing"
                  alt="Digital marketing illustration"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Our Marketing Services
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Multi‑channel strategies to accelerate your digital growth
        </Typography>
        <Grid container spacing={4}>
          {services.map((service, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                sx={{
                  p: 3,
                  height: '100%',
                  boxShadow: softShadow,
                  transition: '0.3s',
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: softShadowHover },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    width: 56,
                    height: 56,
                    mb: 2,
                  }}
                >
                  {service.icon}
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  {service.title}
                </Typography>
                <Typography color="text.secondary">{service.desc}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.light, 0.05), py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            Why Partner with Us?
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Marketing that delivers measurable results
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card sx={{ p: 3, textAlign: 'center', height: '100%', boxShadow: softShadow }}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {benefit.title}
                  </Typography>
                  <Typography color="text.secondary">{benefit.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Platforms & Tools */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Platforms & Tools
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          We use industry‑leading tools to maximize your marketing ROI
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          {platforms.map((platform, idx) => (
            <Chip
              key={idx}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {platform.icon}
                  <span>{platform.name}</span>
                </Box>
              }
              variant="outlined"
              sx={{
                py: 2.5,
                px: 1.5,
                fontSize: '0.9rem',
                fontWeight: 500,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                bgcolor: 'white',
                boxShadow: softShadow,
              }}
            />
          ))}
        </Box>
      </Container>

      {/* Process */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.light, 0.05), py: 8 }} id="process">
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
            Our Marketing Process
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            A proven framework for sustainable growth
          </Typography>
          <Grid container spacing={4}>
            {process.map((step, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Card sx={{ p: 3, textAlign: 'center', height: '100%', boxShadow: softShadow }}>
                  <Typography variant="h2" sx={{ color: 'primary.main', fontWeight: 800, mb: 2 }}>
                    {step.step}
                  </Typography>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    {step.title}
                  </Typography>
                  <Typography color="text.secondary">{step.desc}</Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Metrics / Stats */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Results That Speak for Themselves
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Proven track record of driving measurable growth
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={6} md={3} textAlign="center">
            <Typography variant="h2" color="primary.main" fontWeight={800}>
              150+
            </Typography>
            <Typography color="text.secondary">Successful Campaigns</Typography>
          </Grid>
          <Grid item xs={6} md={3} textAlign="center">
            <Typography variant="h2" color="primary.main" fontWeight={800}>
              300%
            </Typography>
            <Typography color="text.secondary">Average Traffic Increase</Typography>
          </Grid>
          <Grid item xs={6} md={3} textAlign="center">
            <Typography variant="h2" color="primary.main" fontWeight={800}>
              4.8x
            </Typography>
            <Typography color="text.secondary">Average ROI</Typography>
          </Grid>
          <Grid item xs={6} md={3} textAlign="center">
            <Typography variant="h2" color="primary.main" fontWeight={800}>
              95%
            </Typography>
            <Typography color="text.secondary">Client Retention Rate</Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Call to Action */}
      <Box
        sx={{
          bgcolor: 'secondary.main',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom fontWeight={700}>
            Ready to Grow Your Business?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
            Let's create a custom marketing strategy that drives real results.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{ bgcolor: 'white', color: 'secondary.main', px: 5, py: 1.5 }}
          >
            Start Your Marketing Plan
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default DigitalMarketing;